import React from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip, Legend
} from 'recharts';
import Icon from '../../../components/AppIcon';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
      <p className="text-xs font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: entry?.color }} />
          <span className="text-xs text-muted-foreground">{entry?.name}:</span>
          <span className="text-xs font-medium text-foreground">{entry?.value}</span>
        </div>
      ))}
    </div>
  );
};

const MetricCompareRow = ({ label, userValue, avgValue, unit = '' }) => {
  const diff = userValue - avgValue;
  const isAbove = diff >= 0;
  const pct = avgValue > 0 ? Math.abs((diff / avgValue) * 100) : 0;

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-foreground data-text">{userValue}{unit}</span>
        <span className="text-xs text-muted-foreground">vs {avgValue}{unit} avg</span>
        <span className={`text-xs font-semibold ${
          isAbove ? 'text-emerald-400' : 'text-red-400'
        }`}>
          {isAbove ? '+' : '-'}{pct?.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

const EngagementBenchmarks = ({ userMetrics, platformAverages, loading }) => {
  const radarData = [
    { subject: 'Submissions', user: userMetrics?.submissionCount || 0, avg: platformAverages?.submissionCount || 5 },
    { subject: 'Approval Rate', user: userMetrics?.approvalRate || 0, avg: platformAverages?.approvalRate || 70 },
    { subject: 'Earnings', user: Math.min((userMetrics?.totalEarned || 0) / 10, 100), avg: Math.min((platformAverages?.totalEarned || 0) / 10, 100) },
    { subject: 'Score', user: Math.min((userMetrics?.score || 0) / 10, 100), avg: Math.min((platformAverages?.score || 0) / 10, 100) },
    { subject: 'Campaigns', user: Math.min((userMetrics?.campaignCount || 0) * 10, 100), avg: Math.min((platformAverages?.campaignCount || 0) * 10, 100) },
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center">
            <Icon name="Activity" size={18} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground font-heading">Engagement Benchmarks</h3>
            <p className="text-xs text-muted-foreground">Your metrics vs platform averages</p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="h-48 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Radar Chart */}
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={radarData} margin={{ top: 0, right: 20, bottom: 0, left: 20 }}>
                <PolarGrid stroke="rgba(51,65,85,0.6)" />
                <PolarAngleAxis
                  dataKey="subject"
                  tick={{ fill: '#94A3B8', fontSize: 10 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Radar
                  name="Platform Avg"
                  dataKey="avg"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.1}
                  strokeWidth={1.5}
                  strokeDasharray="4 2"
                />
                <Radar
                  name="You"
                  dataKey="user"
                  stroke="#14B8A6"
                  fill="#14B8A6"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
                <Legend
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', color: '#94A3B8' }}
                />
              </RadarChart>
            </ResponsiveContainer>

            {/* Metric Rows */}
            <div className="mt-3">
              <MetricCompareRow
                label="Approval Rate"
                userValue={userMetrics?.approvalRate || 0}
                avgValue={platformAverages?.approvalRate || 70}
                unit="%"
              />
              <MetricCompareRow
                label="Avg Earning/Campaign"
                userValue={parseFloat((userMetrics?.avgEarningPerCampaign || 0)?.toFixed(2))}
                avgValue={parseFloat((platformAverages?.avgEarningPerCampaign || 0)?.toFixed(2))}
                unit="$"
              />
              <MetricCompareRow
                label="SocialCents Score"
                userValue={userMetrics?.score || 0}
                avgValue={platformAverages?.score || 250}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EngagementBenchmarks;
